import { Fragment } from 'react';

import { weekDayArray } from 'app/components/commons/Experience/Availability/utils';
import type { Bed, BedCategory, RoomCategory } from 'app/typings';
import { BABY_COT_BED_CATEGORY_ID } from 'app/typings';
import type { ExtranetExperience } from 'app/typings/packages';
import { pluralize } from 'app/utils/strings';

export const formattedPriceValues = (
  price: number,
  priceDescription: string | null,
  currencyFormatter: (value: number) => string
) => {
  const priceValue = currencyFormatter(price || 0);

  return `${priceValue} ${priceDescription ?? ''}`;
};

export const formattedExpAddOnRules = (experience: ExtranetExperience) => {
  const hasMaxQuantity = !!experience.maxQuantity;
  const hasLastMinuteRule = !!experience.closedHoursBeforeCheckin;
  const hasOnlyOnSpecificDates =
    experience.availableDates && experience.availableDates.length > 0;
  const hasOnlyOnSpecificDays =
    experience.availableDays &&
    experience.availableDays.length > 0 &&
    experience.availableDays.length < weekDayArray.length;

  const validRules = [
    hasMaxQuantity && 'Maximum quantity rule',
    hasLastMinuteRule && 'Last minute rule',
    hasOnlyOnSpecificDays && 'Only on specific days',
    hasOnlyOnSpecificDates && 'Only on specific dates',
  ].filter(Boolean) as string[];

  const validRulesCount = validRules.length;

  if (validRulesCount === 1) {
    return validRules[0];
  }

  if (validRulesCount > 0) {
    return pluralize(
      validRulesCount,
      'availability rule',
      'availability rules'
    );
  }

  return undefined;
};

export const formattedRoomValue = (
  beds: Bed[],
  bedCategories: BedCategory[]
) => {
  let guestCapacity = 0;
  let babyCapacity = 0;

  beds.forEach((bed) => {
    if (!bed.categoryId) {
      return;
    }

    const category = bedCategories.find((cat) => cat.id === bed.categoryId);
    const capacity = category?.capacity || 0;

    if (bed.categoryId === BABY_COT_BED_CATEGORY_ID) {
      babyCapacity += capacity;
    } else {
      guestCapacity += capacity;
    }
  });

  const guestText = pluralize(guestCapacity, 'guest', 'guests');
  const babyText =
    babyCapacity > 0 ? ` + ${pluralize(babyCapacity, 'baby', 'babies')}` : '';

  return (
    <>
      {guestText}
      {babyText}
    </>
  );
};

export const formattedBeds = (beds: Bed[], bedCategories: BedCategory[]) => {
  const bedNames = beds
    .map((bed) =>
      bedCategories?.find((category) => category?.id === bed.categoryId)
    )
    .filter(Boolean)
    .sort((a, b) => (a?.priority ?? 0) - (b?.priority ?? 0))
    .map((category) => category?.name);

  return (
    <>
      {bedNames.map((name, index) => (
        <Fragment key={index}>
          {name}
          <br />
        </Fragment>
      ))}
    </>
  );
};

export const formatRoomTitle = (
  categoryId: number,
  categories: RoomCategory[],
  title?: string
) => {
  if (title) {
    return title;
  }

  return categories?.find((exp) => exp?.id === categoryId)?.name ?? '';
};
